import React from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { Box, Tooltip } from '@mui/material';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GridOnIcon from '@mui/icons-material/GridOn';
import {
  annotationState,
  downloadSnapshotState,
  rotationCCWState,
  rotationCWState
} from 'features/three-view/state/threeAtoms';
import { projectionModeState } from 'features/three-view/state/viewModeAtoms';
import { handleAnnotationClick } from 'features/perspective/utils/annotationHandler';
import { PerspectiveButton } from './PerspectiveButton';

const buttonContainerSx = {
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  justifyContent: 'start',
  height: '100%',
};

const buttonSx = {
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: 'auto',
  display: 'flex',
  padding: 0,
  flexGrow: 1,
  '& .MuiButton-startIcon': {
    margin: 0,
  },
};

const iconSx = { color: 'white' };
const iconActiveSx = { color: '#8adb00' };

export const PerspectivePanel: React.FC = () => {
  const setAnnotation = useSetRecoilState(annotationState);
  const [rotationCW, setRotationCW] = useRecoilState(rotationCWState);
  const [rotationCCW, setRotationCCW] = useRecoilState(rotationCCWState);
  const [downloadSnapshot, setDownloadSnapshot] = useRecoilState(downloadSnapshotState);
  const [projectionMode, setProjectionMode] = useRecoilState(projectionModeState);

  const isPersp = projectionMode === 'perspective';
  const isOrtho = projectionMode === 'orthographic';

  return (
    <Box component="div" sx={buttonContainerSx}>
      <Tooltip title="Apply +isometoric view" arrow>
        <PerspectiveButton
          sx={buttonSx}
          startIcon={<ViewInArIcon sx={iconSx} />}
          onClick={() => handleAnnotationClick('piso', setAnnotation)}
          aria-label="Apply +isometoric view"
        />
      </Tooltip>
      <Tooltip title="Apply -isometoric view" arrow>
        <PerspectiveButton
          sx={buttonSx}
          startIcon={<ViewInArIcon sx={{ ...iconSx, transform: 'scaleX(-1)' }} />}
          onClick={() => handleAnnotationClick('miso', setAnnotation)}
          aria-label="Apply -isometoric view"
        />
      </Tooltip>
      <Tooltip title="Rotate 90° clockwise" arrow>
        <PerspectiveButton
          sx={buttonSx}
          startIcon={<Rotate90DegreesCwIcon sx={iconSx} />}
          onClick={() => setRotationCCW(rotationCCW + 1)}
          aria-label="Rotate 90° clockwise"
        />
      </Tooltip>
      <Tooltip title="Rotate 90° counterclockwise" arrow>
        <PerspectiveButton
          sx={buttonSx}
          startIcon={<Rotate90DegreesCcwIcon sx={iconSx} />}
          onClick={() => setRotationCW(rotationCW + 1)}
          aria-label="Rotate 90° counterclockwise"
        />
      </Tooltip>
      <Tooltip title="Capture screenshot to a file" arrow>
        <PerspectiveButton
          sx={buttonSx}
          startIcon={<CameraAltIcon sx={iconSx} />}
          onClick={() => setDownloadSnapshot(downloadSnapshot + 1)}
          aria-label="Capture screenshot to a file"
        />
      </Tooltip>
      <Tooltip title="Reset view to default" arrow>
        <PerspectiveButton
          sx={buttonSx}
          startIcon={<RestartAltIcon sx={iconSx} />}
          onClick={() => handleAnnotationClick('default', setAnnotation)}
          aria-label="Reset view to default"
        />
      </Tooltip>
      <Tooltip title="Switch to perspective projection" arrow>
        <PerspectiveButton
          sx={buttonSx}
          startIcon={<VisibilityIcon sx={isPersp ? iconActiveSx : iconSx} />}
          onClick={() => setProjectionMode('perspective')}
          aria-label="Switch to perspective projection"
        />
      </Tooltip>
      <Tooltip title="Switch to orthographic projection" arrow>
        <PerspectiveButton
          sx={buttonSx}
          startIcon={<GridOnIcon sx={isOrtho ? iconActiveSx : iconSx} />}
          onClick={() => setProjectionMode('orthographic')}
          aria-label="Switch to orthographic projection"
        />
      </Tooltip>
    </Box >
  );
};
